import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Pause, Play, RotateCcw, Square } from "lucide-react";
import DynamicList from "../../components/dynamic/List";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { ApiService } from "../../services/api";
import checkPermission from "../../utils/permission";
import { useAuth } from "../../contexts/AuthContext";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";
import {
  getLivePresenceSnapshot,
  clearActiveFocusSessionSummary,
  formatSeconds,
  getLiveFocusSessionSnapshot,
  getModuleLabel,
  normalizeActivePresenceSummary,
  normalizeActiveFocusSessionSummary,
  readActivePresenceSummary,
  readActiveFocusSessionSummary,
  storeActivePresenceSummary,
  storeActiveFocusSessionSummary,
} from "../../utils/timeTracking";

type FocusTabKey = "timer" | "stopwatch" | "pomodoro" | "sessions" | "stats";
type ActivityTabKey = "general" | "modules" | "trends";
type LeaderboardTabKey = "activity" | "focus";
type AdminTabKey = "activity" | "focus";
type PeriodKey = "today" | "week" | "month" | "year";
type LeaderboardMetricKey = "activeSeconds" | "focusSeconds" | "completedFocusCount" | "completedPomodoroCount";
type SessionTypeFilterKey = "all" | "timer" | "stopwatch" | "pomodoro";

const FOCUS_TAB_STORAGE_KEY = "timeCenter.focusTab";
const ACTIVITY_TAB_STORAGE_KEY = "timeCenter.activityTab";
const TIMER_FORM_STORAGE_KEY = "timeCenter.timerForm";
const SESSION_FILTER_STORAGE_KEY = "timeCenter.sessionFilter";
const ACTIVITY_MODULE_RANGE_STORAGE_KEY = "timeCenter.activityModuleRange";
const ACTIVITY_TREND_RANGE_STORAGE_KEY = "timeCenter.activityTrendRange";

const TIMER_PRESETS = [300, 600, 1500, 2700, 3600];

const pomodoroStages = [
  { label: "Odak 1", type: "focus", duration: 1500 },
  { label: "Kısa Mola 1", type: "short-break", duration: 300 },
  { label: "Odak 2", type: "focus", duration: 1500 },
  { label: "Kısa Mola 2", type: "short-break", duration: 300 },
  { label: "Odak 3", type: "focus", duration: 1500 },
  { label: "Kısa Mola 3", type: "short-break", duration: 300 },
  { label: "Odak 4", type: "focus", duration: 1500 },
  { label: "Uzun Mola", type: "long-break", duration: 900 },
];

const focusTypeLabels: Record<string, string> = {
  timer: "Zamanlayıcı",
  stopwatch: "Kronometre",
  pomodoro: "Pomodoro",
};

const focusStatusLabels: Record<string, string> = {
  running: "Çalışıyor",
  paused: "Duraklatıldı",
  cancelled: "İptal Edildi",
  completed: "Tamamlandı",
  ended: "Sonlandırıldı",
};

const metricLabels: Record<LeaderboardMetricKey, string> = {
  activeSeconds: "Aktif Süre",
  focusSeconds: "Odak Süresi",
  completedFocusCount: "Tamamlanan Oturum",
  completedPomodoroCount: "Tamamlanan Pomodoro",
};

const focusEventLabels: Record<string, string> = {
  started: "Başlatıldı",
  paused: "Duraklatıldı",
  resumed: "Devam Ettirildi",
  cancelled: "İptal Edildi",
  completed: "Tamamlandı",
  ended: "Sonlandırıldı",
  phase_started: "Faz Başladı",
  phase_completed: "Faz Tamamlandı",
  phase_skipped: "Faz Geçildi",
};

const toDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPresetWindow = (period: PeriodKey) => {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  if (period === "week") {
    const day = start.getDay() === 0 ? 7 : start.getDay();
    start.setDate(start.getDate() - (day - 1));
  } else if (period === "month") {
    start.setDate(1);
  } else {
    start.setMonth(0, 1);
  }
  return { startDate: toDateString(start), endDate: toDateString(end) };
};

const getFocusTypeLabel = (type?: string) => focusTypeLabels[type || ""] || "Odak Oturumu";
const getFocusStatusLabel = (status?: string) => focusStatusLabels[status || ""] || "Bilinmiyor";
const getMetricLabel = (metric: LeaderboardMetricKey) => metricLabels[metric] || "Metrik";
const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Henüz Yok";
const formatTrendDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";
const getFocusEventLabel = (eventType?: string) => focusEventLabels[eventType || ""] || "Kayıt";
const readStoredValue = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const withFormattedMetrics = (data: any = {}) => ({
  ...data,
  activeSeconds: Number(data.activeSeconds) || 0,
  focusSeconds: Number(data.focusSeconds) || 0,
  formattedActive: formatSeconds(Number(data.activeSeconds) || 0),
  formattedFocus: formatSeconds(Number(data.focusSeconds) || 0),
});

const addMetricDelta = (data: any, metricKey: "activeSeconds" | "focusSeconds", delta: number) => {
  const next = { ...(data || {}) };
  next[metricKey] = (Number(next[metricKey]) || 0) + delta;
  if (metricKey === "activeSeconds") {
    next.formattedActive = formatSeconds(next.activeSeconds || 0);
  } else {
    next.formattedFocus = formatSeconds(next.focusSeconds || 0);
  }
  return next;
};

const isDateInRange = (dateValue?: string | null, startDate?: string | null, endDate?: string | null) => {
  if (!dateValue || !startDate || !endDate) return false;
  return dateValue >= startDate && dateValue <= endDate;
};

const applyDeltaToSeries = (series: any[] = [], date: string, moduleKey: string, metricKey: "activeSeconds" | "focusSeconds", delta: number) => {
  const nextSeries = Array.isArray(series) ? [...series] : [];
  const index = nextSeries.findIndex((item) => item.date === date && item.moduleKey === moduleKey);
  if (index >= 0) {
    nextSeries[index] = addMetricDelta(nextSeries[index], metricKey, delta);
  } else {
    nextSeries.push(
      withFormattedMetrics({
        date,
        moduleKey,
        activeSeconds: metricKey === "activeSeconds" ? delta : 0,
        focusSeconds: metricKey === "focusSeconds" ? delta : 0,
        completedFocusCount: 0,
        completedPomodoroCount: 0,
        focusSessionCount: 0,
      })
    );
  }
  return nextSeries.sort((left, right) => String(left.date).localeCompare(String(right.date)));
};

const applyDeltaToModuleBreakdown = (items: any[] = [], moduleKey: string, metricKey: "activeSeconds" | "focusSeconds", delta: number) => {
  const nextItems = Array.isArray(items) ? [...items] : [];
  const index = nextItems.findIndex((item) => item.moduleKey === moduleKey);
  if (index >= 0) {
    nextItems[index] = addMetricDelta(nextItems[index], metricKey, delta);
  } else {
    nextItems.push(
      withFormattedMetrics({
        moduleKey,
        activeSeconds: metricKey === "activeSeconds" ? delta : 0,
        focusSeconds: metricKey === "focusSeconds" ? delta : 0,
        completedFocusCount: 0,
        completedPomodoroCount: 0,
        focusSessionCount: 0,
      })
    );
  }
  return nextItems.sort((left, right) => (right[metricKey] || 0) - (left[metricKey] || 0));
};

const applyLiveFocusStats = (stats: any, liveSession: any) => {
  if (!stats || !liveSession?.startedAt) return stats;
  const baseline = Number(stats.currentSession?.elapsedSeconds || 0);
  const delta = Math.max(0, Number(liveSession.liveElapsedSeconds || 0) - baseline);
  if (!delta) return stats;
  const sessionDate = toDateString(new Date(liveSession.startedAt));
  const todayWindow = getPresetWindow("today");
  const weekWindow = getPresetWindow("week");
  const monthWindow = getPresetWindow("month");
  const yearWindow = getPresetWindow("year");
  const nextStats = {
    ...stats,
    selectedRange: {
      ...(stats.selectedRange || {}),
      summary: isDateInRange(sessionDate, stats.selectedRange?.startDate, stats.selectedRange?.endDate)
        ? addMetricDelta(stats.selectedRange?.summary, "focusSeconds", delta)
        : stats.selectedRange?.summary,
    },
    today: isDateInRange(sessionDate, todayWindow.startDate, todayWindow.endDate) ? addMetricDelta(stats.today, "focusSeconds", delta) : stats.today,
    week: isDateInRange(sessionDate, weekWindow.startDate, weekWindow.endDate) ? addMetricDelta(stats.week, "focusSeconds", delta) : stats.week,
    month: isDateInRange(sessionDate, monthWindow.startDate, monthWindow.endDate) ? addMetricDelta(stats.month, "focusSeconds", delta) : stats.month,
    year: isDateInRange(sessionDate, yearWindow.startDate, yearWindow.endDate) ? addMetricDelta(stats.year, "focusSeconds", delta) : stats.year,
    dailySeries: applyDeltaToSeries(stats.dailySeries, sessionDate, liveSession.moduleKey || "time-center", "focusSeconds", delta),
    currentSession: {
      ...(stats.currentSession || {}),
      elapsedSeconds: liveSession.liveElapsedSeconds || 0,
      remainingSeconds: liveSession.liveRemainingSeconds ?? null,
      formattedElapsed: formatSeconds(liveSession.liveElapsedSeconds || 0),
      formattedRemaining:
        liveSession.liveRemainingSeconds === null || liveSession.liveRemainingSeconds === undefined ? null : formatSeconds(liveSession.liveRemainingSeconds || 0),
    },
  };
  return nextStats;
};

const applyLiveActivityStats = (stats: any, livePresence: any) => {
  if (!stats || !livePresence?.startedAt) return stats;
  const baseline = Number(stats.currentPresence?.activeSeconds || 0);
  const delta = Math.max(0, Number(livePresence.liveActiveSeconds || 0) - baseline);
  if (!delta) return stats;
  const sessionDate = toDateString(new Date(livePresence.startedAt));
  const moduleKey = livePresence.moduleKey || "other";
  const todayWindow = getPresetWindow("today");
  const weekWindow = getPresetWindow("week");
  const monthWindow = getPresetWindow("month");
  const yearWindow = getPresetWindow("year");
  const nextStats = {
    ...stats,
    selectedRange: {
      ...(stats.selectedRange || {}),
      summary: isDateInRange(sessionDate, stats.selectedRange?.startDate, stats.selectedRange?.endDate)
        ? addMetricDelta(stats.selectedRange?.summary, "activeSeconds", delta)
        : stats.selectedRange?.summary,
    },
    today: isDateInRange(sessionDate, todayWindow.startDate, todayWindow.endDate) ? addMetricDelta(stats.today, "activeSeconds", delta) : stats.today,
    week: isDateInRange(sessionDate, weekWindow.startDate, weekWindow.endDate) ? addMetricDelta(stats.week, "activeSeconds", delta) : stats.week,
    month: isDateInRange(sessionDate, monthWindow.startDate, monthWindow.endDate) ? addMetricDelta(stats.month, "activeSeconds", delta) : stats.month,
    year: isDateInRange(sessionDate, yearWindow.startDate, yearWindow.endDate) ? addMetricDelta(stats.year, "activeSeconds", delta) : stats.year,
    byModule: applyDeltaToModuleBreakdown(stats.byModule, moduleKey, "activeSeconds", delta),
    dailySeries: applyDeltaToSeries(stats.dailySeries, sessionDate, moduleKey, "activeSeconds", delta),
    currentPresence: {
      ...(stats.currentPresence || {}),
      activeSeconds: livePresence.liveActiveSeconds || 0,
      formattedActive: formatSeconds(livePresence.liveActiveSeconds || 0),
      moduleKey,
      pathname: livePresence.pathname,
      startedAt: livePresence.startedAt,
    },
  };
  return nextStats;
};

const groupSeriesByDate = (series: any[] = [], metricKey: "activeSeconds" | "focusSeconds") => {
  const grouped = new Map<string, any>();
  for (const item of series || []) {
    const dateKey = item.date;
    if (!dateKey) continue;
    const current =
      grouped.get(dateKey) || {
        date: dateKey,
        moduleCount: 0,
        activeSeconds: 0,
        focusSeconds: 0,
        modules: [],
      };
    current[metricKey] += Number(item[metricKey]) || 0;
    const moduleIndex = current.modules.findIndex((moduleItem: any) => moduleItem.moduleKey === item.moduleKey);
    if (moduleIndex >= 0) {
      current.modules[moduleIndex][metricKey] += Number(item[metricKey]) || 0;
    } else {
      current.modules.push({
        moduleKey: item.moduleKey,
        activeSeconds: metricKey === "activeSeconds" ? Number(item.activeSeconds) || 0 : 0,
        focusSeconds: metricKey === "focusSeconds" ? Number(item.focusSeconds) || 0 : 0,
      });
    }
    current.moduleCount = current.modules.length;
    grouped.set(dateKey, current);
  }
  return Array.from(grouped.values())
    .map((item) => ({
      ...item,
      formattedValue: formatSeconds(item[metricKey] || 0),
      modules: item.modules
        .map((moduleItem: any) => ({
          ...moduleItem,
          formattedValue: formatSeconds(moduleItem[metricKey] || 0),
        }))
        .sort((left: any, right: any) => (right[metricKey] || 0) - (left[metricKey] || 0)),
    }))
    .sort((left, right) => String(right.date).localeCompare(String(left.date)));
};

const SummaryGrid = ({ items }: { items: Array<{ title: string; value: string; subtitle?: string }> }) => (
  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
    {items.map((item) => (
      <div
        key={item.title}
        className="group rounded-xl border border-slate-200 bg-white px-5 py-4 transition duration-200 hover:border-slate-300">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{item.title}</div>
        <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">{item.value}</div>
        {item.subtitle ? <div className="mt-1 text-xs text-slate-500">{item.subtitle}</div> : null}
      </div>
    ))}
  </div>
);

const SecondaryTabs = <T extends string>({
  title,
  tabs,
  active,
  onChange,
}: {
  title: string;
  tabs: Array<{ key: T; label: string; disabled?: boolean }>;
  active: T;
  onChange: (value: T) => void;
}) => (
  <div className="mb-6 border-b border-slate-200">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
      <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-800 sm:pb-4">{title}</span>
      <div className="-mb-px flex max-w-full flex-nowrap items-end gap-6 overflow-x-auto scrollbar-hide sm:gap-7">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              if (!tab.disabled) onChange(tab.key);
            }}
            disabled={tab.disabled}
            title={tab.disabled ? "Aktif odak oturumu tamamlanmadan bu sekmeye geçilemez." : undefined}
            className={`relative flex-shrink-0 whitespace-nowrap border-b-2 pb-3 text-sm transition ${
              tab.disabled
                ? "cursor-not-allowed border-transparent text-slate-300"
                : active === tab.key
                  ? "border-indigo-600 font-semibold text-slate-900"
                  : "border-transparent font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
            }`}>
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const SectionShell = ({
  title,
  tabs,
  activeTab,
  onTabChange,
  children,
}: {
  title: string;
  tabs: Array<{ key: any; label: string }>;
  activeTab: any;
  onTabChange: (value: any) => void;
  children: React.ReactNode;
}) => (
  <div className="flex h-full flex-col bg-transparent">
    <div className="min-h-0 flex-1 px-3 py-3 sm:px-4 sm:py-4">
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex min-h-0 w-full flex-1 flex-col px-5 py-5 sm:px-7 sm:py-6">
          <SecondaryTabs title={title} tabs={tabs} active={activeTab} onChange={onTabChange} />
          <div className="min-h-0 flex-1 overflow-y-auto pr-1 scrollbar-hide">
            <div className="space-y-6 pb-8">{children}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MetricBars = ({
  items,
  labelKey,
  valueKey,
  formatter,
  emptyText,
}: {
  items: any[];
  labelKey: string;
  valueKey: string;
  formatter: (value: number) => string;
  emptyText: string;
}) => {
  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300/90 bg-slate-50/50 px-6 py-10 text-center text-sm text-slate-500">{emptyText}</div>
    );
  }

  const max = Math.max(1, ...items.map((item) => Number(item[valueKey]) || 0));

  return (
    <div className="space-y-2.5">
      {items.map((item) => {
        const value = Number(item[valueKey]) || 0;
        return (
          <div
            key={`${item[labelKey]}-${value}`}
            className="rounded-xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="truncate text-sm font-semibold text-slate-800">{item[labelKey]}</div>
              <div className="shrink-0 text-xs font-semibold tabular-nums text-slate-700">{formatter(value)}</div>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-indigo-500"
                style={{ width: `${Math.max(4, Math.round((value / max) * 100))}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SessionBadge = ({ text, tone = "gray" }: { text: string; tone?: "blue" | "amber" | "emerald" | "rose" | "gray" }) => {
  const toneClasses =
    tone === "blue"
      ? "bg-indigo-50 text-indigo-800 border-indigo-200"
      : tone === "amber"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : tone === "emerald"
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : tone === "rose"
            ? "bg-rose-50 text-rose-700 border-rose-200"
            : "bg-gray-50 text-gray-700 border-gray-200";

  return <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${toneClasses}`}>{text}</span>;
};

const InputLabel = ({ children }: { children: React.ReactNode }) => (
  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{children}</label>
);

const DateRangeFields = ({
  value,
  onChange,
}: {
  value: { startDate: string; endDate: string };
  onChange: (value: { startDate: string; endDate: string }) => void;
}) => (
  <div className="grid gap-3 md:grid-cols-2">
    <div>
      <p className="text-[11px] text-slate-400 mb-1">Başlangıç</p>
      <input
        type="date"
        value={value.startDate}
        onChange={(e) => onChange({ ...value, startDate: e.target.value })}
        max={value.endDate || undefined}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] text-slate-800 outline-none transition focus:border-slate-400"
      />
    </div>
    <div>
      <p className="text-[11px] text-slate-400 mb-1">Bitiş</p>
      <input
        type="date"
        value={value.endDate}
        onChange={(e) => onChange({ ...value, endDate: e.target.value })}
        min={value.startDate || undefined}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] text-slate-800 outline-none transition focus:border-slate-400"
      />
    </div>
  </div>
);

export default function TimeCenterPage() {
  const { user } = useAuth();
  const canView = checkPermission("time_tracking_list");
  const canAdmin = user?.isSuperAdmin || checkPermission("time_tracking_admin_view");
  const [focusTab, setFocusTab] = useState<FocusTabKey>(() => readStoredValue<FocusTabKey>(FOCUS_TAB_STORAGE_KEY, "stopwatch"));
  const [activityTab, setActivityTab] = useState<ActivityTabKey>(() => readStoredValue<ActivityTabKey>(ACTIVITY_TAB_STORAGE_KEY, "general"));
  const [sessionFilter, setSessionFilter] = useState<SessionTypeFilterKey>(() => readStoredValue<SessionTypeFilterKey>(SESSION_FILTER_STORAGE_KEY, "all"));
  const [activityModuleRange, setActivityModuleRange] = useState(() => readStoredValue<{ startDate: string; endDate: string }>(ACTIVITY_MODULE_RANGE_STORAGE_KEY, getPresetWindow("month")));
  const [activityTrendRange, setActivityTrendRange] = useState(() => readStoredValue<{ startDate: string; endDate: string }>(ACTIVITY_TREND_RANGE_STORAGE_KEY, getPresetWindow("month")));
  const [timerHours, setTimerHours] = useState(() => readStoredValue<number>(TIMER_FORM_STORAGE_KEY, 1500) >= 3600 ? Math.floor(readStoredValue<number>(TIMER_FORM_STORAGE_KEY, 1500) / 3600) : 0);
  const [timerMinutes, setTimerMinutes] = useState(() => Math.floor((readStoredValue<number>(TIMER_FORM_STORAGE_KEY, 1500) % 3600) / 60));
  const [timerSeconds, setTimerSeconds] = useState(() => readStoredValue<number>(TIMER_FORM_STORAGE_KEY, 1500) % 60);
  const [pomodoroCustomNames, setPomodoroCustomNames] = useState<string[]>(() => {
    // Force reset to 4 phases if there are 8 in storage
    const stored = readStoredValue<string[]>("timeCenter.pomodoroNames", ["Matematik", "Kısa Mola", "Fizik", "Uzun Mola"]);
    if (stored.length === 8) {
      // Reset to 4 phases
      const newNames = ["Matematik", "Kısa Mola", "Fizik", "Uzun Mola"];
      localStorage.setItem("timeCenter.pomodoroNames", JSON.stringify(newNames));
      return newNames;
    }
    return stored;
  });
  const [pomodoroCustomDurations, setPomodoroCustomDurations] = useState<number[]>(() => {
    // Force reset to 4 phases if there are 8 in storage
    const stored = readStoredValue<number[]>("timeCenter.pomodoroDurations", [1500, 300, 1500, 900]);
    if (stored.length === 8) {
      // Reset to 4 phases
      const newDurations = [1500, 300, 1500, 900]; // 25min, 5min, 25min, 15min
      localStorage.setItem("timeCenter.pomodoroDurations", JSON.stringify(newDurations));
      return newDurations;
    }
    return stored;
  });
  const [isEditingSteps, setIsEditingSteps] = useState(false);
  const [tempDurationValues, setTempDurationValues] = useState<{[key: number]: string}>({});
  const [activeSummary, setActiveSummary] = useState<any>(() => readActiveFocusSessionSummary());
  const [activePresenceSummary, setActivePresenceSummary] = useState<any>(() => readActivePresenceSummary());
  const [focusHistory, setFocusHistory] = useState<any[]>([]);
  const [focusStats, setFocusStats] = useState<any>(null);
  const [activityStats, setActivityStats] = useState<any>(null);
  const [activityModuleStats, setActivityModuleStats] = useState<any>(null);
  const [activityTrendStats, setActivityTrendStats] = useState<any>(null);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [leaderboardTab, setLeaderboardTab] = useState<LeaderboardTabKey>("activity");
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<PeriodKey>("week");
  const [leaderboardModuleKey, setLeaderboardModuleKey] = useState<string>("all");
  const [focusLeaderboardMetric, setFocusLeaderboardMetric] = useState<LeaderboardMetricKey>("focusSeconds");
  const [adminDepartmentId, setAdminDepartmentId] = useState<string | null>(null);
  const [adminPeriod, setAdminPeriod] = useState<PeriodKey>("week");
  const [adminTab, setAdminTab] = useState<AdminTabKey>("activity");
  const [expandedSessionIds, setExpandedSessionIds] = useState<string[]>([]);
  const [expandedTrendDates, setExpandedTrendDates] = useState<string[]>([]);
  const [, setTick] = useState(0);
  const autoTransitionLockRef = useRef(false);

  const liveSession = getLiveFocusSessionSnapshot(activeSummary);
  const livePresence = getLivePresenceSnapshot(activePresenceSummary);

  const syncSummary = useCallback((session: any, terminal = false) => {
    const normalized = terminal ? null : normalizeActiveFocusSessionSummary(session);
    setActiveSummary(normalized);
    if (normalized) storeActiveFocusSessionSummary(normalized);
    else clearActiveFocusSessionSummary();
  }, []);

  const reloadFocus = useCallback(async () => {
    const [active, history, stats] = await Promise.all([
      ApiService.getMyFocusSessions({ status: "active", page: 1, limit: 1 }).catch(() => ({ data: [] })),
      ApiService.getMyFocusSessions({ page: 1, limit: 50 }).catch(() => ({ data: [] })),
      ApiService.getMyFocusStats().catch(() => null),
    ]);
    syncSummary(active?.data?.[0] || null, !active?.data?.[0]);
    setFocusHistory(history?.data || []);
    setFocusStats(stats);
  }, [syncSummary]);

  const reloadActivityGeneral = useCallback(async () => {
    const activity = await ApiService.getMyActivityStats().catch(() => null);
    setActivityStats(activity);
    setActivePresenceSummary(normalizeActivePresenceSummary(activity?.currentPresence) || readActivePresenceSummary());
    if (activity?.currentPresence) {
      storeActivePresenceSummary(normalizeActivePresenceSummary(activity.currentPresence));
    }
  }, []);

  const reloadActivityModules = useCallback(async () => {
    const response = await ApiService.getMyActivityStats(activityModuleRange).catch(() => null);
    setActivityModuleStats(response);
  }, [activityModuleRange]);

  const reloadActivityTrends = useCallback(async () => {
    const response = await ApiService.getMyActivityStats(activityTrendRange).catch(() => null);
    setActivityTrendStats(response);
  }, [activityTrendRange]);

  useEffect(() => {
    if (!canView) return;
    void Promise.all([reloadFocus(), reloadActivityGeneral(), reloadActivityModules(), reloadActivityTrends()]);
  }, [canView, reloadActivityGeneral, reloadActivityModules, reloadActivityTrends, reloadFocus]);

  useEffect(() => {
    const interval = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(FOCUS_TAB_STORAGE_KEY, JSON.stringify(focusTab));
    } catch {}
  }, [focusTab]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVITY_TAB_STORAGE_KEY, JSON.stringify(activityTab));
    } catch {}
  }, [activityTab]);

  useEffect(() => {
    try {
      localStorage.setItem(SESSION_FILTER_STORAGE_KEY, JSON.stringify(sessionFilter));
    } catch {}
  }, [sessionFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(TIMER_FORM_STORAGE_KEY, JSON.stringify(timerHours * 3600 + timerMinutes * 60 + timerSeconds));
    } catch {}
  }, [timerHours, timerMinutes, timerSeconds]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVITY_MODULE_RANGE_STORAGE_KEY, JSON.stringify(activityModuleRange));
    } catch {}
  }, [activityModuleRange]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVITY_TREND_RANGE_STORAGE_KEY, JSON.stringify(activityTrendRange));
    } catch {}
  }, [activityTrendRange]);

  useEffect(() => {
    try {
      localStorage.setItem("timeCenter.pomodoroNames", JSON.stringify(pomodoroCustomNames));
    } catch {}
  }, [pomodoroCustomNames]);

  useEffect(() => {
    try {
      localStorage.setItem("timeCenter.pomodoroDurations", JSON.stringify(pomodoroCustomDurations));
    } catch {}
  }, [pomodoroCustomDurations]);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      setActiveSummary(normalizeActiveFocusSessionSummary(customEvent.detail) || null);
    };
    window.addEventListener("activeFocusSessionChanged", handler);
    return () => window.removeEventListener("activeFocusSessionChanged", handler);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      setActivePresenceSummary(normalizeActivePresenceSummary(customEvent.detail) || null);
    };
    window.addEventListener("activePresenceChanged", handler);
    return () => window.removeEventListener("activePresenceChanged", handler);
  }, []);

  useEffect(() => {
    const handler = () => {
      void Promise.all([reloadFocus(), reloadActivityGeneral(), reloadActivityModules(), reloadActivityTrends()]);
    };
    window.addEventListener("focusTrackingDataChanged", handler);
    return () => window.removeEventListener("focusTrackingDataChanged", handler);
  }, [reloadActivityGeneral, reloadActivityModules, reloadActivityTrends, reloadFocus]);

  useEffect(() => {
    if (!canView) return;
    void reloadActivityModules();
  }, [activityModuleRange, canView, reloadActivityModules]);

  useEffect(() => {
    if (!canView) return;
    void reloadActivityTrends();
  }, [activityTrendRange, canView, reloadActivityTrends]);

  useEffect(() => {
    if (!canView) return;
    const interval = window.setInterval(() => {
      void Promise.all([reloadFocus(), reloadActivityGeneral()]);
    }, 30000);
    return () => window.clearInterval(interval);
  }, [canView, reloadActivityGeneral, reloadFocus]);

  useEffect(() => {
    const loadLeaderboard = async () => {
      if (!canView) return;
      const metric = leaderboardTab === "activity" ? "activeSeconds" : focusLeaderboardMetric;
      const response = await ApiService.getLeaderboard({
        ...getPresetWindow(leaderboardPeriod),
        moduleKey: leaderboardTab === "activity" && leaderboardModuleKey !== "all" ? leaderboardModuleKey : undefined,
        metric,
        limit: 20,
      }).catch(() => []);
      setLeaderboard(Array.isArray(response) ? response : []);
    };
    void loadLeaderboard();
  }, [leaderboardPeriod, leaderboardModuleKey, leaderboardTab, focusLeaderboardMetric, canView]);

  const startFocusSession = useCallback(
    async (type: "timer" | "stopwatch" | "pomodoro", payload: any) => {
      const session = await ApiService.startFocusSession({
        type,
        source: "time-center",
        moduleKey: "time-center",
        ...payload,
      });
      syncSummary(session);
      await reloadFocus();
    },
    [reloadFocus, syncSummary]
  );

  const recordLiveEvent = useCallback(
    async (
      eventType: "paused" | "resumed" | "cancelled" | "completed" | "ended" | "phase_skipped",
      extra?: any
    ) => {
      if (!liveSession?.id) return null;
      const response = await ApiService.recordFocusEvent(liveSession.id, {
        eventType,
        elapsedSeconds: liveSession.liveElapsedSeconds,
        remainingSeconds: extra?.remainingSeconds !== undefined ? extra.remainingSeconds : liveSession.liveRemainingSeconds ?? null,
        status: extra?.status,
        metadata: extra?.metadata || liveSession.metadata || {},
      });
      syncSummary(response, ["cancelled", "completed", "ended"].includes(eventType));
      await reloadFocus();
      return response;
    },
    [liveSession, reloadFocus, syncSummary]
  );

  const toggleSessionDetails = useCallback((sessionId: string) => {
    setExpandedSessionIds((current) => (current.includes(sessionId) ? current.filter((item) => item !== sessionId) : [...current, sessionId]));
  }, []);

  const toggleTrendDetails = useCallback((date: string) => {
    setExpandedTrendDates((current) => (current.includes(date) ? current.filter((item) => item !== date) : [...current, date]));
  }, []);

  const fetchAdminReportRows = useCallback(
    async (page: number) => {
      if (!adminDepartmentId) {
        return {
          data: [],
          meta: {
            page: 1,
            totalPages: 1,
            totalItems: 0,
            limit: 10,
          },
        };
      }
      const response = await ApiService.getAdminUsersActivityList({
        page,
        limit: 10,
        departmentId: adminDepartmentId,
      }).catch(() => ({ data: [], meta: { page: 1, totalPages: 1, totalItems: 0, limit: 10 } }));
      const rows = await Promise.all(
        (response?.data || []).map(async (item: any) => {
          const overview = await ApiService.getAdminUserActivityOverview(item.id, getPresetWindow(adminPeriod)).catch(() => null);
          return {
            id: item.id,
            user: item,
            overview,
          };
        })
      );
      return {
        data: rows,
        meta: response?.meta || {
          page,
          totalPages: 1,
          totalItems: rows.length,
          limit: 10,
        },
      };
    },
    [adminDepartmentId, adminPeriod]
  );

  const renderAdminReportRow = useCallback(
    (item: any) => {
      const fullName = `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim() || "İsimsiz Kullanıcı";
      if (adminTab === "activity") {
        return (
          <div key={item.id} className="grid grid-cols-[minmax(220px,1.6fr)_minmax(120px,0.8fr)_minmax(140px,0.9fr)_minmax(140px,0.9fr)_90px] gap-3 border-b border-gray-200 px-4 py-4 text-sm">
            <div className="min-w-0">
              <div className="truncate font-semibold text-gray-900">{fullName}</div>
              <div className="mt-1 truncate text-xs text-gray-500">{item.user?.email || "-"}</div>
            </div>
            <div className="truncate text-gray-600">{item.user?.department?.name || "Departman Yok"}</div>
            <div className="font-semibold text-gray-900">{item.overview?.activity?.selectedRange?.summary?.formattedActive || "00:00"}</div>
            <div className="font-semibold text-gray-900">{item.overview?.activity?.year?.formattedActive || "00:00"}</div>
            <div className="font-semibold text-gray-900">{item.overview?.leaderboardEntry ? `#${item.overview.leaderboardEntry.rank}` : "-"}</div>
          </div>
        );
      }
      return (
        <div key={item.id} className="grid grid-cols-[minmax(220px,1.5fr)_minmax(130px,0.9fr)_120px_140px_120px] gap-3 border-b border-gray-200 px-4 py-4 text-sm">
          <div className="min-w-0">
            <div className="truncate font-semibold text-gray-900">{fullName}</div>
            <div className="mt-1 truncate text-xs text-gray-500">{item.user?.email || "-"}</div>
          </div>
          <div className="font-semibold text-gray-900">{item.overview?.focus?.selectedRange?.summary?.formattedFocus || "00:00"}</div>
          <div className="font-semibold text-gray-900">{item.overview?.focus?.completion?.completedSessions || 0}</div>
          <div className="font-semibold text-gray-900">{item.overview?.focus?.selectedRange?.summary?.completedPomodoroCount || 0}</div>
          <div className="font-semibold text-gray-900">%{item.overview?.focus?.completion?.completionRate || 0}</div>
        </div>
      );
    },
    [adminTab]
  );

  useEffect(() => {
    if (!liveSession) return;
    if (!["timer", "stopwatch", "pomodoro"].includes(focusTab)) return;
    if (focusTab !== liveSession.type) {
      setFocusTab(liveSession.type as FocusTabKey);
    }
  }, [focusTab, liveSession]);

  if (!canView) {
    return (
      <div className="flex min-h-[calc(100dvh-4rem)] items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white px-8 py-10 text-center shadow-sm">
          <p className="text-sm leading-relaxed text-slate-600">Bu alana erişim yetkiniz bulunmuyor.</p>
        </div>
      </div>
    );
  }

  const timerTotal = timerHours * 3600 + timerMinutes * 60 + timerSeconds;
  const activeLabel = liveSession ? `${getFocusTypeLabel(liveSession.type)} • ${getFocusStatusLabel(liveSession.status)}` : "Aktif Oturum Yok";
  const displayedFocusStats = applyLiveFocusStats(focusStats, liveSession);
  const displayedActivityStats = applyLiveActivityStats(activityStats, livePresence);
  const displayedActivityModuleStats = applyLiveActivityStats(activityModuleStats, livePresence);
  const displayedActivityTrendStats = applyLiveActivityStats(activityTrendStats, livePresence);
  const groupedFocusTrend = groupSeriesByDate(displayedFocusStats?.dailySeries || [], "focusSeconds");
  const groupedActivityTrend = groupSeriesByDate(displayedActivityTrendStats?.dailySeries || [], "activeSeconds");
  const filteredFocusHistory = focusHistory.filter((item) => sessionFilter === "all" || item.type === sessionFilter);
  const focusTabs: Array<{ key: FocusTabKey; label: string; disabled?: boolean }> = [
    { key: "stopwatch", label: "Kronometre", disabled: Boolean(liveSession && liveSession.type !== "stopwatch") },
    { key: "timer", label: "Zamanlayıcı", disabled: Boolean(liveSession && liveSession.type !== "timer") },
    { key: "pomodoro", label: "Pomodoro", disabled: Boolean(liveSession && liveSession.type !== "pomodoro") },
    { key: "sessions", label: "Oturumlar" },
    { key: "stats", label: "İstatistikler" },
  ];

  const renderTimerPanel = () => {
    const isLiveTimer = liveSession?.type === "timer";
    const goalSeconds = isLiveTimer ? Math.max(1, Number(liveSession.goalDurationSeconds) || 0) : Math.max(1, timerTotal || 1);
    const displayRemaining = isLiveTimer ? liveSession.liveRemainingSeconds || 0 : timerTotal;
    const displayElapsed = isLiveTimer ? liveSession.liveElapsedSeconds || 0 : 0;
    const isRunning = isLiveTimer && liveSession.status === "running";
    const isPaused = isLiveTimer && liveSession.status === "paused";
    
    // Progress ring - shows CONSUMED time, not remaining (reverse of stopwatch)
    const consumedProgress = isLiveTimer ? Math.min(1, Math.max(0, displayElapsed / goalSeconds)) : 0;
    const remainingProgress = isLiveTimer ? Math.min(1, Math.max(0, displayRemaining / goalSeconds)) : timerTotal > 0 ? 1 : 0.1;

    const ringSize = 280;
    const ringStroke = 6;
    const ringRadius = (ringSize - ringStroke) / 2 - 4;
    const ringCirc = 2 * Math.PI * ringRadius;
    const consumedOffset = ringCirc * (1 - consumedProgress);
    const remainingOffset = ringCirc * (1 - remainingProgress);

    const displaySeconds = isLiveTimer ? displayRemaining : timerTotal;
    const hours = Math.floor(displaySeconds / 3600);
    const minutes = Math.floor((displaySeconds % 3600) / 60);
    const seconds = displaySeconds % 60;
    const pad = (value: number) => value.toString().padStart(2, "0");

    return (
      <div className="space-y-6">
        {isLiveTimer ? (
          // Live Timer View
          <div className="flex flex-col items-center py-6">
            <div className="relative flex items-center justify-center">
              <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`} className="-rotate-90" aria-hidden>
                {/* Background ring */}
                <circle cx={ringSize / 2} cy={ringSize / 2} r={ringRadius} fill="none" stroke="currentColor" strokeWidth={ringStroke} className="text-slate-100" />
                {/* Remaining time ring */}
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={ringRadius}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={ringStroke}
                  strokeLinecap="round"
                  strokeDasharray={ringCirc}
                  strokeDashoffset={remainingOffset}
                  className={`transition-[stroke-dashoffset] duration-500 ease-linear ${isRunning ? "text-emerald-500" : isPaused ? "text-amber-500" : "text-slate-300"}`}
                />
                {/* Consumed time ring (overlay) */}
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={ringRadius - 8}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeDasharray={ringCirc * 0.95}
                  strokeDashoffset={consumedOffset * 0.95}
                  className={`transition-[stroke-dashoffset] duration-500 ease-linear ${isRunning ? "text-indigo-400" : isPaused ? "text-indigo-300" : "text-slate-300"}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <span className="mb-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">Kalan</span>
                <div className="flex items-baseline gap-0.5 font-mono font-semibold tabular-nums text-slate-900">
                  {hours > 0 ? (
                    <>
                      <span className="text-4xl sm:text-5xl">{pad(hours)}</span>
                      <span className="text-2xl text-slate-300 sm:text-3xl">:</span>
                    </>
                  ) : null}
                  <span className="text-4xl sm:text-5xl">{pad(minutes)}</span>
                  <span className="text-2xl text-slate-300 sm:text-3xl">:</span>
                  <span className="text-4xl sm:text-5xl">{pad(seconds)}</span>
                </div>
                <div className="mt-3 flex items-center gap-2 text-[11px]">
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${isRunning ? "animate-pulse bg-emerald-500" : isPaused ? "bg-amber-500" : "bg-slate-400"}`} />
                  <span className="font-medium text-slate-600">Geçen: {formatSeconds(displayElapsed)}</span>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {isRunning ? (
                <button
                  type="button"
                  onClick={() => void recordLiveEvent("paused", { status: "paused" })}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50">
                  <Pause className="h-4 w-4" />
                  Duraklat
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void recordLiveEvent("resumed", { status: "running" })}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3 text-sm font-semibold text-white shadow-sm shadow-emerald-600/20 transition hover:bg-blue-700">
                  <Play className="h-4 w-4" />
                  Devam Et
                </button>
              )}
              <button
                type="button"
                onClick={() => void recordLiveEvent("cancelled", { status: "cancelled", remainingSeconds: liveSession.liveRemainingSeconds || 0 })}
                className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-6 py-3 text-sm font-semibold text-rose-700 shadow-sm transition hover:bg-rose-50">
                <Square className="h-4 w-4" />
                İptal
              </button>
            </div>

            <p className="mt-4 max-w-md text-center text-xs leading-relaxed text-slate-500">
              Süre sıfırlandığında oturum otomatik tamamlanır. İç halka geçen süreyi gösterir.
            </p>
          </div>
        ) : (
          // Timer Setup View - Modern Design
          <div className="space-y-6">

            {/* Main Content Card */}
            <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
              <div className="grid gap-8 lg:grid-cols-2">
                
                {/* Left Side - Controls */}
                <div className="space-y-6">
                  {/* Time Input */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-900">Süre Ayarla</h4>
                    <div className="grid grid-cols-3 gap-3">

                      {/* Hours */}
                      <div className="text-center">
                        <label className="block text-xs font-medium text-slate-500 mb-2">Saat</label>
                        <input
                          type="number"
                          min={0}
                          max={23}
                          value={timerHours}
                          onChange={(e) => setTimerHours(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-center font-mono text-2xl font-semibold text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                      
                      {/* Minutes */}
                      <div className="text-center">
                        <label className="block text-xs font-medium text-slate-500 mb-2">Dakika</label>
                        <input
                          type="number"
                          min={0}
                          max={59}
                          value={timerMinutes}
                          onChange={(e) => setTimerMinutes(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-center font-mono text-2xl font-semibold text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                      
                      {/* Seconds */}
                      <div className="text-center">
                        <label className="block text-xs font-medium text-slate-500 mb-2">Saniye</label>
                        <input
                          type="number"
                          min={0}
                          max={59}
                          value={timerSeconds}
                          onChange={(e) => setTimerSeconds(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-center font-mono text-2xl font-semibold text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quick Presets */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-900">Hızlı Seçim</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {TIMER_PRESETS.map((value) => {
                        const isSelected = timerTotal === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              setTimerHours(Math.floor(value / 3600));
                              setTimerMinutes(Math.floor((value % 3600) / 60));
                              setTimerSeconds(value % 60);
                            }}
                            className={`rounded-xl border px-4 py-3 font-medium transition ${
                              isSelected
                                ? "border-blue-200 bg-blue-50 text-blue-700"
                                : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
                            }`}>
                            {formatSeconds(value)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Start Button */}
                  <button
                    type="button"
                    disabled={Boolean(liveSession) || timerTotal <= 0}
                    onClick={() =>
                      void startFocusSession("timer", {
                        goalDurationSeconds: timerTotal,
                        remainingSeconds: timerTotal,
                        elapsedSeconds: 0,
                        metadata: { label: "Zamanlayıcı" },
                      })
                    }
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-4 font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed">
                    <Play className="h-5 w-5" />
                    Zamanlayıcıyı Başlat
                  </button>
                </div>

                {/* Right Side - Preview */}
                <div className="flex flex-col items-center justify-center">
                  <div className="text-center space-y-4">
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Önizleme</div>
                    <div className="relative">
                      <div className="flex items-center justify-center w-40 h-40 rounded-full border-4 border-slate-200 bg-slate-50">
                        <div className="text-center">
                          <div className="font-mono text-2xl font-bold text-slate-900">{formatSeconds(timerTotal)}</div>
                          <div className="text-xs font-medium text-slate-500 mt-1">Hedef Süre</div>
                        </div>
                      </div>
                      {timerTotal > 0 && (
                        <div className="absolute inset-0 rounded-full border-4 border-blue-500 opacity-80"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStopwatchPanel = () => {
    const isLive = liveSession?.type === "stopwatch";
    const elapsed = isLive ? Number(liveSession.liveElapsedSeconds || 0) : 0;
    const isRunning = isLive && liveSession.status === "running";
    const isPaused = isLive && liveSession.status === "paused";

    const ringSize = 340;
    const ringStroke = 5;
    const ringRadius = (ringSize - ringStroke) / 2 - 4;
    const ringCirc = 2 * Math.PI * ringRadius;
    const ringProgress = isLive ? (elapsed % 60) / 60 : 0;
    const ringOffset = ringCirc * (1 - ringProgress);

    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    const pad = (value: number) => value.toString().padStart(2, "0");

    return (
      <div className="flex flex-col items-center py-4 sm:py-8">
        <div className="relative flex items-center justify-center">
          <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`} className="-rotate-90" aria-hidden>
            <circle cx={ringSize / 2} cy={ringSize / 2} r={ringRadius} fill="none" stroke="currentColor" strokeWidth={ringStroke} className="text-slate-100" />
            <circle
              cx={ringSize / 2}
              cy={ringSize / 2}
              r={ringRadius}
              fill="none"
              stroke="currentColor"
              strokeWidth={ringStroke}
              strokeLinecap="round"
              strokeDasharray={ringCirc}
              strokeDashoffset={ringOffset}
              className={`transition-[stroke-dashoffset] duration-300 ease-linear ${isRunning ? "text-indigo-600" : isPaused ? "text-amber-500" : "text-slate-300"}`}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="mb-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">Kronometre</span>
            <div className="flex items-baseline gap-0.5 font-mono font-semibold tabular-nums text-slate-900">
              {hours > 0 ? (
                <>
                  <span className="text-5xl sm:text-6xl">{pad(hours)}</span>
                  <span className="text-3xl text-slate-300 sm:text-4xl">:</span>
                </>
              ) : null}
              <span className="text-5xl sm:text-6xl">{pad(minutes)}</span>
              <span className="text-3xl text-slate-300 sm:text-4xl">:</span>
              <span className="text-5xl sm:text-6xl">{pad(seconds)}</span>
            </div>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-600">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${isRunning ? "animate-pulse bg-emerald-500" : isPaused ? "bg-amber-500" : "bg-slate-400"}`} />
              {isRunning ? "Çalışıyor" : isPaused ? "Duraklatıldı" : "Hazır"}
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 mt-6">
          {isRunning ? (
            <>
              <button
                onClick={() => recordLiveEvent("paused")}
                className="h-10 px-5 rounded-lg flex items-center gap-2 text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              >
                Duraklat
              </button>
              <button
                onClick={() => recordLiveEvent("cancelled")}
                className="h-10 px-5 rounded-lg flex items-center gap-2 text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              >
                Bitir
              </button>
            </>
          ) : isPaused ? (
            <>
              <button
                onClick={() => recordLiveEvent("resumed")}
                className="h-10 px-5 rounded-lg flex items-center gap-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors"
              >
                Devam
              </button>
              <button
                onClick={() => recordLiveEvent("cancelled")}
                className="h-10 px-5 rounded-lg flex items-center gap-2 text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              >
                Bitir
              </button>
            </>
          ) : (
            <button
              onClick={() => startFocusSession("stopwatch", {})}
              className="h-10 px-6 rounded-lg flex items-center gap-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors"
            >
              Başlat
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPomodoroPanel = () => {
    const isLivePomodoro = liveSession?.type === "pomodoro";
    const currentPhaseIndex = Number(liveSession?.metadata?.currentPhaseIndex || 0);
    const currentStage = pomodoroStages[currentPhaseIndex];
    const isRunning = isLivePomodoro && liveSession.status === "running";
    const isPaused = isLivePomodoro && liveSession.status === "paused";
    
    const displayRemaining = isLivePomodoro ? liveSession.liveRemainingSeconds || 0 : pomodoroStages[0].duration;
    const displayElapsed = isLivePomodoro ? liveSession.liveElapsedSeconds || 0 : 0;
    const phaseDuration = currentStage?.duration || pomodoroStages[0].duration;
    const phaseProgress = isLivePomodoro ? Math.min(1, Math.max(0, displayElapsed / phaseDuration)) : 0;

    const ringSize = 280;
    const ringStroke = 8;
    const ringRadius = (ringSize - ringStroke) / 2 - 4;
    const ringCirc = 2 * Math.PI * ringRadius;
    const progressOffset = ringCirc * (1 - phaseProgress);

    const minutes = Math.floor(displayRemaining / 60);
    const seconds = displayRemaining % 60;
    const pad = (value: number) => value.toString().padStart(2, "0");

    // Use custom names and durations from state - dynamic length
    const customStages = pomodoroCustomNames.map((name, index) => {
      // Determine type based on index (focus, short-break, long-break pattern)
      let type: "focus" | "short-break" | "long-break";
      if (index % 2 === 0) {
        type = "focus"; // Even indexes are focus
      } else if (index === pomodoroCustomNames.length - 1 && pomodoroCustomNames.length > 3) {
        type = "long-break"; // Last item is long-break if more than 3 items
      } else {
        type = "short-break"; // Other odd indexes are short-break
      }
      
      return {
        label: name,
        type,
        duration: pomodoroCustomDurations[index] || 1500 // Default to 25 minutes
      };
    });

    const updateCustomName = (index: number, name: string) => {
      const newNames = [...pomodoroCustomNames];
      newNames[index] = name;
      setPomodoroCustomNames(newNames);
    };

    const updateCustomDuration = (index: number, minutes: number) => {
      const newDurations = [...pomodoroCustomDurations];
      // Allow any positive number, but ensure it's at least 1 when saving
      const validMinutes = Math.max(1, Math.min(120, minutes));
      newDurations[index] = validMinutes * 60; // Convert to seconds
      setPomodoroCustomDurations(newDurations);
    };

    const resetToDefaults = () => {
      // Index 0 (çift) = focus: "Matematik"  ✓
      // Index 1 (tek) = short-break: "Kısa Mola"  ✓ 
      // Index 2 (çift) = focus: "Fizik"  ✓
      // Index 3 (tek, son) = long-break: "Uzun Mola"  ✓
      setPomodoroCustomNames(["Matematik", "Kısa Mola", "Fizik", "Uzun Mola"]);
      setPomodoroCustomDurations([1500, 300, 1500, 900]); // 25min, 5min, 25min, 15min
      setIsEditingSteps(false);
    };

    const addPhase = () => {
      const lastStage = customStages[customStages.length - 1];
      const newType = lastStage?.type === "focus" ? "short-break" : "focus";
      const newName = newType === "focus" ? `Odak ${Math.floor((customStages.length + 1) / 2) + 1}` : `Mola ${Math.floor(customStages.length / 2) + 1}`;
      const newDuration = newType === "focus" ? 1500 : 300; // 25 min or 5 min
      
      setPomodoroCustomNames(prev => [...prev, newName]);
      setPomodoroCustomDurations(prev => [...prev, newDuration]);
    };

    const removePhase = (index: number) => {
      if (pomodoroCustomNames.length <= 2) return; // Minimum 2 phase kalsın
      
      const newNames = [...pomodoroCustomNames];
      const newDurations = [...pomodoroCustomDurations];
      newNames.splice(index, 1);
      newDurations.splice(index, 1);
      
      setPomodoroCustomNames(newNames);
      setPomodoroCustomDurations(newDurations);
    };

    return (
      <div className="space-y-6">
        {isLivePomodoro ? (
          // Live Session View
          <div className="space-y-4">
            {/* Current Step Info with Controls */}
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
              {/* Left: Step Info */}
              <div className="flex items-center gap-3">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-white ${
                  customStages[currentPhaseIndex]?.type === "focus" ? "bg-blue-600" : "bg-slate-500"
                }`}>
                  {currentPhaseIndex + 1}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{customStages[currentPhaseIndex]?.label || "Adım 1"}</div>
                  <div className="text-xs text-slate-500">
                    {customStages[currentPhaseIndex]?.type === "focus" ? "Odak" : "Mola"} - 
                    Adım {currentPhaseIndex + 1} / {customStages.length}
                  </div>
                </div>
              </div>

              {/* Center: Controls */}
              <div className="flex items-center gap-2">
                {/* Main Action */}
                {isRunning ? (
                  <button
                    onClick={() => void recordLiveEvent("paused", { status: "paused" })}
                    className="group relative rounded-lg bg-slate-100 px-3 py-2 transition-all hover:bg-slate-200 flex items-center gap-2">
                    <Pause className="h-4 w-4 text-slate-700" />
                    <span className="text-sm font-medium text-slate-700">Duraklat</span>
                  </button>
                ) : (
                  <button
                    onClick={() => void recordLiveEvent("resumed", { status: "running" })}
                    className="group relative rounded-lg bg-blue-600 px-3 py-2 transition-all hover:bg-blue-700 flex items-center gap-2">
                    <Play className="h-4 w-4 text-white" />
                    <span className="text-sm font-medium text-white">Devam</span>
                  </button>
                )}

                {/* Stop Session */}
                <button
                  onClick={() => void recordLiveEvent("cancelled", { status: "cancelled", remainingSeconds: liveSession.liveRemainingSeconds || 0 })}
                  className="group rounded-lg bg-rose-100 px-3 py-2 transition-all hover:bg-rose-200 flex items-center gap-2">
                  <Square className="h-4 w-4 text-rose-600" />
                  <span className="text-sm font-medium text-rose-600">Bitir</span>
                </button>

                {/* Next Step */}
                <button
                  onClick={async () => {
                    if (!liveSession?.id) return;
                    autoTransitionLockRef.current = true;
                    try {
                      const currentPhaseIndex = Number(liveSession.metadata?.currentPhaseIndex || 0);
                      const nextStage = customStages[currentPhaseIndex + 1];
                      await recordLiveEvent("phase_skipped", { status: "running", remainingSeconds: 0, metadata: { ...liveSession.metadata, skippedPhaseIndex: currentPhaseIndex } });
                      if (nextStage) {
                        const response = await ApiService.recordFocusEvent(liveSession.id, {
                          eventType: "phase_started",
                          elapsedSeconds: liveSession.liveElapsedSeconds,
                          remainingSeconds: nextStage.duration,
                          status: "running",
                          metadata: { ...liveSession.metadata, stages: customStages, currentPhaseIndex: currentPhaseIndex + 1, currentPhaseLabel: nextStage.label, currentPhaseType: nextStage.type, currentPhaseDurationSeconds: nextStage.duration },
                        });
                        syncSummary(response);
                        await reloadFocus();
                      }
                    } catch (error) {
                      console.error("Error in next step:", error);
                    } finally {
                      autoTransitionLockRef.current = false;
                    }
                  }}
                  className="group rounded-lg bg-indigo-100 px-3 py-2 transition-all hover:bg-indigo-200 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm font-medium text-indigo-600">Sonraki</span>
                </button>
              </div>

            </div>

            {/* Timer */}
            <div className="relative flex flex-col items-center py-4">
              <div className="relative mb-4">
                <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`} className="-rotate-90" aria-hidden>
                  <circle cx={ringSize / 2} cy={ringSize / 2} r={ringRadius} fill="none" stroke="currentColor" strokeWidth={ringStroke} className="text-slate-100" />
                  <circle
                    cx={ringSize / 2}
                    cy={ringSize / 2}
                    r={ringRadius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={ringStroke}
                    strokeLinecap="round"
                    strokeDasharray={ringCirc}
                    strokeDashoffset={progressOffset}
                    className={`transition-[stroke-dashoffset] duration-500 ease-linear ${isRunning ? "text-blue-600" : isPaused ? "text-amber-500" : "text-slate-300"}`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <div className="flex items-baseline gap-1 font-mono font-semibold tabular-nums text-slate-900">
                    <span className="text-5xl sm:text-6xl">{pad(minutes)}</span>
                    <span className="text-3xl text-slate-300 sm:text-4xl">:</span>
                    <span className="text-5xl sm:text-6xl">{pad(seconds)}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <div className={`h-1.5 w-1.5 rounded-full ${isRunning ? "animate-pulse bg-emerald-500" : isPaused ? "bg-amber-500" : "bg-slate-400"}`} />
                    <span className="font-medium text-slate-600">
                      {isRunning ? "Çalışıyor" : isPaused ? "Duraklatıldı" : "Hazır"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Total Time - Bottom Right */}
              <div className="absolute bottom-4 right-4 rounded-lg bg-white/80 px-3 py-2 text-right backdrop-blur-sm border border-slate-200">
                <div className="text-xs font-medium text-slate-500">Toplam</div>
                <div className="font-mono text-sm font-semibold text-slate-700">
                  {formatSeconds(customStages.reduce((acc, stage) => acc + stage.duration, 0))}
                </div>
              </div>

              {/* Progress Steps */}
              <div className="w-full max-w-2xl">
                <div className="relative">
                  <div className="absolute left-6 right-6 top-3 h-px bg-slate-200" />
                  <div 
                    className="absolute left-6 top-3 h-px bg-blue-600 transition-all duration-500"
                    style={{ width: `${Math.max(0, ((currentPhaseIndex) / (customStages.length - 1)) * 100)}%` }}
                  />
                  
                  <div className="flex justify-between">
                    {customStages.map((stage, index) => {
                      const isCompleted = index < currentPhaseIndex;
                      const isCurrent = index === currentPhaseIndex;
                      return (
                        <div key={index} className="flex flex-col items-center gap-2">
                          <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                            isCompleted 
                              ? "bg-blue-600 text-white"
                              : isCurrent 
                                ? "border-2 border-violet-600 bg-white text-blue-600" 
                                : "border border-slate-300 bg-white text-slate-400"
                          }`}>
                            {isCompleted ? "✓" : index + 1}
                          </div>
                          <div className={`max-w-16 truncate text-center text-xs font-medium ${
                            isCurrent ? "text-blue-700" : isCompleted ? "text-slate-600" : "text-slate-400"
                          }`}>
                            {stage.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>


          </div>
        ) : (
          // Setup View
          <div className="space-y-6">

            {/* Step Names & Durations Editor */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-slate-900">Adım Özelleştirme</h4>
                  <p className="text-sm text-slate-500">Her adımın adını ve süresini düzenleyebilirsiniz</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsEditingSteps(!isEditingSteps)}
                    className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                      isEditingSteps
                        ? "border-blue-200 bg-blue-50 text-blue-700"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}>
                    {isEditingSteps ? "Kaydet" : "Düzenle"}
                  </button>
                  <button
                    onClick={resetToDefaults}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
                    Sıfırla
                  </button>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {customStages.map((stage, index) => (
                  <div
                    key={index}
                    className="group relative rounded-2xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all duration-200">
                    
                    {/* Top badge and delete button */}
                    <div className="mb-3 flex items-start justify-between">
                      <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                        stage.type === "focus" 
                          ? "bg-blue-100 text-blue-700" 
                          : stage.type === "long-break"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-slate-100 text-slate-600"
                      }`}>
                        <span className="font-semibold">{index + 1}</span>
                        <span>{stage.type === "focus" ? "Odak" : stage.type === "long-break" ? "Uzun Mola" : "Mola"}</span>
                      </div>
                      {isEditingSteps && pomodoroCustomNames.length > 2 && (
                        <button
                          onClick={() => removePhase(index)}
                          className="opacity-0 group-hover:opacity-100 flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-all duration-200"
                          title="Bu fazı sil">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    
                    {/* Stage Name */}
                    <div className="mb-3">
                      {isEditingSteps ? (
                        <input
                          type="text"
                          value={pomodoroCustomNames[index] || ""}
                          onChange={(e) => updateCustomName(index, e.target.value)}
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-medium text-slate-900 outline-none transition-all focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
                          placeholder={pomodoroStages[index]?.label || "Adım adı"}
                        />
                      ) : (
                        <h3 className="font-semibold text-slate-900 text-base leading-tight">{stage.label}</h3>
                      )}
                    </div>

                    {/* Stage Duration */}
                    <div className="flex items-center justify-between">
                      {isEditingSteps ? (
                        <div className="flex items-center gap-2 w-full">
                          <input
                            type="number"
                            min={1}
                            max={120}
                            value={tempDurationValues[index] !== undefined ? tempDurationValues[index] : Math.round(stage.duration / 60)}
                            onChange={(e) => {
                              const value = e.target.value;
                              setTempDurationValues(prev => ({ ...prev, [index]: value }));
                            }}
                            onBlur={(e) => {
                              const value = Number(e.target.value) || 1;
                              updateCustomDuration(index, value);
                              setTempDurationValues(prev => {
                                const newValues = { ...prev };
                                delete newValues[index];
                                return newValues;
                              });
                            }}
                            className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-medium text-slate-900 outline-none transition-all focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
                          />
                          <span className="text-sm font-medium text-slate-500 whitespace-nowrap">dakika</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-slate-600">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">{formatSeconds(stage.duration)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add New Phase Card - Only visible when editing */}
                {isEditingSteps && (
                  <button
                    onClick={addPhase}
                    className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/30 p-4 shadow-sm transition-all duration-200 hover:border-blue-400 hover:bg-blue-50/50 hover:shadow-md">
                    <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-slate-500 transition-colors duration-200 group-hover:bg-blue-200 group-hover:text-blue-600">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-slate-700">Yeni Faz</div>
                        <div className="text-xs text-slate-500">Eklemek için tıklayın</div>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Start Button */}
            <div className="text-center">
              <button
                disabled={Boolean(liveSession)}
                onClick={() => void startFocusSession("pomodoro", {
                  goalDurationSeconds: customStages[0].duration,
                  remainingSeconds: customStages[0].duration,
                  elapsedSeconds: 0,
                  metadata: {
                    label: "Pomodoro",
                    customNames: pomodoroCustomNames,
                    customDurations: pomodoroCustomDurations,
                    stages: customStages,
                    currentPhaseIndex: 0,
                    currentPhaseLabel: customStages[0].label,
                    currentPhaseType: customStages[0].type,
                    currentPhaseDurationSeconds: customStages[0].duration
                  }
                })}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-sm shadow-violet-600/25 transition hover:bg-blue-700 disabled:bg-slate-300 disabled:shadow-none">
                <Play className="h-4 w-4" />
                Odaklanmaya Başla
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSessionsPanel = () => {
    const sessionCounts = {
      all: focusHistory.length,
      timer: focusHistory.filter((item) => item.type === "timer").length,
      stopwatch: focusHistory.filter((item) => item.type === "stopwatch").length,
      pomodoro: focusHistory.filter((item) => item.type === "pomodoro").length,
    };
    const filterLabels: Record<string, string> = { all: "Tüm Oturumlar", timer: "Zamanlayıcı", stopwatch: "Kronometre", pomodoro: "Pomodoro" };

    return (
      <div className="space-y-4">
        {/* Header bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-slate-800">Oturumlar</h3>
            <span className="text-xs text-slate-400 font-medium">{sessionCounts[sessionFilter as keyof typeof sessionCounts]} kayıt</span>
          </div>

          {/* Dropdown filter */}
          <div className="relative">
            <button
              onClick={() => {
                const el = document.getElementById("session-filter-dropdown");
                if (el) el.classList.toggle("hidden");
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-slate-300 transition-colors"
            >
              <span>{filterLabels[sessionFilter]}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
            <div
              id="session-filter-dropdown"
              className="hidden absolute right-0 top-full mt-1 w-44 rounded-lg border border-slate-200 bg-white shadow-lg z-20 py-1"
            >
              {(["all", "timer", "stopwatch", "pomodoro"] as const).map((key) => (
                <button
                  key={key}
                  onClick={() => {
                    setSessionFilter(key);
                    document.getElementById("session-filter-dropdown")?.classList.add("hidden");
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors ${
                    sessionFilter === key ? "bg-slate-50 text-slate-900 font-medium" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span>{filterLabels[key]}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                    sessionFilter === key ? "bg-slate-200 text-slate-800" : "bg-slate-100 text-slate-500"
                  }`}>{sessionCounts[key]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sessions List */}
        {filteredFocusHistory.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-10 text-center">
            <div className="mx-auto w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-600 mb-0.5">Henüz oturum yok</p>
            <p className="text-xs text-slate-400">İlk odak oturumunuzu başlatın</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredFocusHistory.map((item) => {
              const isExpanded = expandedSessionIds.includes(item.id);
              const typeColor = item.type === "timer" ? "bg-blue-500" : item.type === "stopwatch" ? "bg-indigo-500" : "bg-violet-500";
              const statusDot = item.status === "completed" ? "bg-emerald-400" : item.status === "cancelled" ? "bg-rose-400" : item.status === "paused" ? "bg-amber-400" : "bg-blue-400";

              return (
                <div key={item.id} className={`rounded-xl border bg-white transition-all ${isExpanded ? "border-slate-300 shadow-sm" : "border-slate-100 hover:border-slate-200"}`}>
                  {/* Main row */}
                  <button
                    onClick={() => toggleSessionDetails(item.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left"
                  >
                    {/* Type indicator */}
                    <div className={`w-1.5 h-8 rounded-full ${typeColor} flex-shrink-0`} />

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{getFocusTypeLabel(item.type)}</span>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot} flex-shrink-0`} />
                        <span className="text-[11px] text-slate-400">{getFocusStatusLabel(item.status)}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-slate-400">{formatDateTime(item.startedAt)}</span>
                        <span className="text-xs font-medium text-slate-600">{item.formattedElapsed || formatSeconds(item.elapsedSeconds || 0)}</span>
                      </div>
                    </div>

                    {/* Chevron */}
                    <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`} />
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 px-4 py-3">
                      {/* Stats row */}
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Süre</div>
                          <div className="text-xs font-semibold text-slate-800">{item.formattedElapsed || formatSeconds(item.elapsedSeconds || 0)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Kalan</div>
                          <div className="text-xs font-semibold text-slate-800">
                            {item.remainingSeconds !== null && item.remainingSeconds !== undefined ? item.formattedRemaining || formatSeconds(item.remainingSeconds) : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Bitiş</div>
                          <div className="text-xs font-semibold text-slate-800">{item.endedAt ? formatDateTime(item.endedAt) : "Devam ediyor"}</div>
                        </div>
                      </div>

                      {/* Events */}
                      {(item.events || []).length > 0 && (
                        <div className="space-y-1.5">
                          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Kayıtlar</div>
                          {(item.events || []).map((event: any) => (
                            <div key={event.id || `${item.id}-${event.occurredAt}-${event.eventType}`} className="flex items-center justify-between py-1.5 px-2 rounded-md bg-slate-50">
                              <div className="flex items-center gap-2">
                                <div className="w-1 h-1 rounded-full bg-slate-400" />
                                <span className="text-xs text-slate-700">{getFocusEventLabel(event.eventType)}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-[11px] text-slate-500">{formatSeconds(event.elapsedSeconds || 0)}</span>
                                <span className="text-[11px] text-slate-400">{formatDateTime(event.occurredAt)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderFocusStatsPanel = () => {
    const completionRate = displayedFocusStats?.completion?.completionRate || 0;
    const totalSessions = displayedFocusStats?.completion?.totalSessions || 0;
    const completedSessions = displayedFocusStats?.completion?.completedSessions || 0;
    const completedPomodoro = displayedFocusStats?.selectedRange?.summary?.completedPomodoroCount || 0;
    const trendItems = groupedFocusTrend.map((item: any) => ({ ...item, label: formatTrendDate(item.date) }));
    const trendMax = Math.max(1, ...trendItems.map((item: any) => Number(item.focusSeconds) || 0));

    return (
      <div className="space-y-8">
        {/* Live status */}
        {liveSession && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-slate-800 animate-pulse" />
            <span className="text-sm text-slate-500">{activeLabel}</span>
          </div>
        )}

        {/* Hero numbers */}
        <div>
          <p className="text-[13px] text-slate-400 mb-4">Odak süreleri</p>
          <div className="flex items-baseline gap-10">
            <div>
              <div className="text-3xl font-light text-slate-900 tabular-nums tracking-tight">{displayedFocusStats?.today?.formattedFocus || "00:00"}</div>
              <div className="text-[13px] text-slate-400 mt-1">bugün</div>
            </div>
            <div>
              <div className="text-3xl font-light text-slate-900 tabular-nums tracking-tight">{displayedFocusStats?.week?.formattedFocus || "00:00"}</div>
              <div className="text-[13px] text-slate-400 mt-1">bu hafta</div>
            </div>
            <div>
              <div className="text-3xl font-light text-slate-900 tabular-nums tracking-tight">{displayedFocusStats?.selectedRange?.summary?.formattedFocus || "00:00"}</div>
              <div className="text-[13px] text-slate-400 mt-1">dönem</div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-100" />

        {/* Stats rows */}
        <div>
          <p className="text-[13px] text-slate-400 mb-4">Oturumlar</p>
          <div className="grid grid-cols-2 gap-x-12 gap-y-4">
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-slate-500">Toplam oturum</span>
              <span className="text-[15px] font-medium text-slate-900 tabular-nums">{totalSessions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-slate-500">Tamamlanan</span>
              <span className="text-[15px] font-medium text-slate-900 tabular-nums">{completedSessions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-slate-500">Başarı oranı</span>
              <span className="text-[15px] font-medium text-slate-900 tabular-nums">%{completionRate}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-slate-500">Pomodoro</span>
              <span className="text-[15px] font-medium text-slate-900 tabular-nums">{completedPomodoro}</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-100" />

        {/* Trend */}
        <div>
          <p className="text-[13px] text-slate-400 mb-4">Günlük trend</p>
          {trendItems.length === 0 ? (
            <p className="text-[13px] text-slate-300">Henüz veri yok</p>
          ) : (
            <div className="space-y-3">
              {trendItems.map((item: any) => {
                const value = Number(item.focusSeconds) || 0;
                const pct = Math.max(2, Math.round((value / trendMax) * 100));
                return (
                  <div key={`${item.label}-${value}`} className="flex items-center gap-4">
                    <span className="text-[13px] text-slate-400 w-12 flex-shrink-0 tabular-nums">{item.label}</span>
                    <div className="flex-1 h-[3px] rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-slate-400" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[13px] font-medium text-slate-700 w-14 flex-shrink-0 text-right tabular-nums">{formatSeconds(value)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const focusPanel = (
    <SectionShell title="Odak" tabs={focusTabs} activeTab={focusTab} onTabChange={setFocusTab}>
      {focusTab === "timer" ? renderTimerPanel() : null}
      {focusTab === "stopwatch" ? renderStopwatchPanel() : null}
      {focusTab === "pomodoro" ? renderPomodoroPanel() : null}
      {focusTab === "sessions" ? renderSessionsPanel() : null}
      {focusTab === "stats" ? renderFocusStatsPanel() : null}
    </SectionShell>
  );

  const activityPanel = (
    <SectionShell title="Aktivite" tabs={[{ key: "general", label: "Genel" }, { key: "modules", label: "Modül Bazlı" }, { key: "trends", label: "Trendler" }]} activeTab={activityTab} onTabChange={setActivityTab}>
      {activityTab === "general" ? (
        <div className="space-y-8">
          <div>
            <p className="text-[13px] text-slate-400 mb-4">Aktif süreler</p>
            <div className="flex items-baseline gap-10 flex-wrap">
              <div>
                <div className="text-3xl font-light text-slate-900 tabular-nums tracking-tight">{displayedActivityStats?.today?.formattedActive || "00:00"}</div>
                <div className="text-[13px] text-slate-400 mt-1">bugün</div>
              </div>
              <div>
                <div className="text-3xl font-light text-slate-900 tabular-nums tracking-tight">{displayedActivityStats?.week?.formattedActive || "00:00"}</div>
                <div className="text-[13px] text-slate-400 mt-1">bu hafta</div>
              </div>
              <div>
                <div className="text-3xl font-light text-slate-900 tabular-nums tracking-tight">{displayedActivityStats?.month?.formattedActive || "00:00"}</div>
                <div className="text-[13px] text-slate-400 mt-1">bu ay</div>
              </div>
              <div>
                <div className="text-3xl font-light text-slate-900 tabular-nums tracking-tight">{displayedActivityStats?.year?.formattedActive || "00:00"}</div>
                <div className="text-[13px] text-slate-400 mt-1">bu yıl</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {activityTab === "modules" ? (
        <div className="space-y-6">
          <DateRangeFields value={activityModuleRange} onChange={setActivityModuleRange} />
          <div className="border-t border-slate-100" />
          {(displayedActivityModuleStats?.byModule || []).length === 0 ? (
            <p className="text-[13px] text-slate-400">Henüz modül bazlı aktivite verisi oluşmadı.</p>
          ) : (() => {
            const modules = (displayedActivityModuleStats?.byModule || []).map((item: any) => ({ ...item, label: getModuleLabel(item.moduleKey) }));
            const max = Math.max(1, ...modules.map((m: any) => Number(m.activeSeconds) || 0));
            return (
              <div className="space-y-3">
                {modules.map((item: any) => {
                  const value = Number(item.activeSeconds) || 0;
                  const pct = Math.max(2, Math.round((value / max) * 100));
                  return (
                    <div key={item.moduleKey} className="flex items-center gap-4">
                      <span className="text-[13px] text-slate-500 w-28 flex-shrink-0 truncate">{item.label}</span>
                      <div className="flex-1 h-[3px] rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-slate-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[13px] font-medium text-slate-700 w-16 flex-shrink-0 text-right tabular-nums">{formatSeconds(value)}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      ) : null}
      {activityTab === "trends" ? (
        <div className="space-y-6">
          <DateRangeFields value={activityTrendRange} onChange={setActivityTrendRange} />
          <div className="border-t border-slate-100" />
          {groupedActivityTrend.length === 0 ? (
            <p className="text-[13px] text-slate-400">Henüz günlük aktivite verisi oluşmadı.</p>
          ) : (
            <div className="space-y-1">
              {groupedActivityTrend.map((item: any) => {
                const isExpanded = expandedTrendDates.includes(item.date);
                return (
                  <div key={item.date}>
                    <button onClick={() => toggleTrendDetails(item.date)} className="w-full flex items-center justify-between py-3 text-left hover:bg-slate-50/50 rounded-lg px-2 -mx-2 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-[13px] font-medium text-slate-700">{formatTrendDate(item.date)}</span>
                        <span className="text-[11px] text-slate-400">{item.moduleCount} modül</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[13px] font-medium text-slate-900 tabular-nums">{item.formattedValue}</span>
                        {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="ml-4 mb-2 space-y-1 border-l border-slate-100 pl-4">
                        {item.modules.map((moduleItem: any) => (
                          <div key={`${item.date}-${moduleItem.moduleKey}`} className="flex items-center justify-between py-1.5">
                            <span className="text-[12px] text-slate-500">{getModuleLabel(moduleItem.moduleKey)}</span>
                            <span className="text-[12px] font-medium text-slate-700 tabular-nums">{moduleItem.formattedValue}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </SectionShell>
  );

  const leaderboardPanel = (
    <div className="flex h-full flex-col bg-transparent">
      <div className="min-h-0 flex-1 px-3 py-3 sm:px-4 sm:py-4">
        <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex w-full min-h-0 flex-1 flex-col px-5 py-5 sm:px-7 sm:py-6">
            <SecondaryTabs title="Liderlik" tabs={[{ key: "activity", label: "Aktivite Liderliği" }, { key: "focus", label: "Odak Liderliği" }]} active={leaderboardTab} onChange={setLeaderboardTab} />

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-4 mb-6">
              <div className="min-w-[140px]">
                <p className="text-[11px] text-slate-400 mb-1">Dönem</p>
                <select value={leaderboardPeriod} onChange={(e) => setLeaderboardPeriod(e.target.value as PeriodKey)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-slate-400"><option value="week">Bu Hafta</option><option value="month">Bu Ay</option><option value="year">Bu Yıl</option></select>
              </div>
              {leaderboardTab === "activity" ? (
                <div className="min-w-[160px]">
                  <p className="text-[11px] text-slate-400 mb-1">Modül</p>
                  <select value={leaderboardModuleKey} onChange={(e) => setLeaderboardModuleKey(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-slate-400"><option value="all">Tüm Modüller</option>{(activityStats?.byModule || []).map((item: any) => <option key={item.moduleKey} value={item.moduleKey}>{getModuleLabel(item.moduleKey)}</option>)}</select>
                </div>
              ) : (
                <div className="min-w-[160px]">
                  <p className="text-[11px] text-slate-400 mb-1">Metrik</p>
                  <select value={focusLeaderboardMetric} onChange={(e) => setFocusLeaderboardMetric(e.target.value as LeaderboardMetricKey)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-slate-400"><option value="focusSeconds">Odak Süresi</option><option value="completedFocusCount">Tamamlanan Oturum</option><option value="completedPomodoroCount">Tamamlanan Pomodoro</option></select>
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 mb-4" />

            {/* List */}
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 scrollbar-hide">
              {leaderboard.length === 0 ? (
                <p className="text-[13px] text-slate-400 py-6 text-center">Seçilen filtreler için liderlik verisi bulunamadı.</p>
              ) : (
                <div className="space-y-0 divide-y divide-slate-100">
                  {leaderboard.map((item) => (
                    <div key={`${item.rank}-${item.user?.id || item.rank}`} className="flex items-center gap-4 py-3">
                      <span className="text-[13px] font-semibold text-slate-400 w-6 text-center tabular-nums">{item.rank}</span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-medium text-slate-900">{item.user ? `${item.user.firstName || ""} ${item.user.lastName || ""}`.trim() : "Kullanıcı"}</div>
                        <div className="truncate text-[11px] text-slate-400">{item.department?.name || ""}</div>
                      </div>
                      <span className="text-[13px] font-semibold text-slate-900 tabular-nums">{item.formattedValue}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const adminPanel = canAdmin ? (
    <div className="flex h-full flex-col bg-transparent">
      <div className="min-h-0 flex-1 px-3 py-3 sm:px-4 sm:py-4">
        <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex w-full min-h-0 flex-1 flex-col px-5 py-5 sm:px-7 sm:py-6">
            <SecondaryTabs title="Raporlar" tabs={[{ key: "activity", label: "Aktivite Raporları" }, { key: "focus", label: "Odak Raporları" }]} active={adminTab} onChange={setAdminTab} />

            {/* Table */}
            <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-100">
              <div className="border-b border-slate-100 bg-slate-50/50 px-4 py-2.5">
                {adminTab === "activity" ? (
                  <div className="grid grid-cols-[minmax(220px,1.6fr)_minmax(120px,0.8fr)_minmax(140px,0.9fr)_minmax(140px,0.9fr)_90px] gap-3 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                    <div>Kullanıcı</div>
                    <div>Departman</div>
                    <div>Dönem Aktif Süre</div>
                    <div>Bu Yıl</div>
                    <div>Sıra</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-[minmax(220px,1.5fr)_minmax(130px,0.9fr)_120px_140px_120px] gap-3 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                    <div>Kullanıcı</div>
                    <div>Dönem Odak Süresi</div>
                    <div>Tamamlanan</div>
                    <div>Pomodoro</div>
                    <div>Oran</div>
                  </div>
                )}
              </div>
              <DynamicList
                header={false}
                title="Rapor Tablosu"
                path="time-center-admin-report-table"
                fetcher={fetchAdminReportRows}
                refreshKey={`${adminDepartmentId || "none"}-${adminPeriod}-${adminTab}`}
                emptyMessage={adminDepartmentId ? "Seçilen departman için rapor verisi bulunamadı." : "Raporları görmek için önce bir departman seçin."}
                item={renderAdminReportRow}
                isPagination={true}
                isSearch={false}
                actions={
                  <div className="flex items-center gap-2">
                    <div className="w-[200px]">
                      <DepartmentSelector value={adminDepartmentId} onChange={(value) => setAdminDepartmentId(value || null)} allowClear={true} placeholder="Departman Seçin" />
                    </div>
                    <select value={adminPeriod} onChange={(e) => setAdminPeriod(e.target.value as PeriodKey)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-slate-700 outline-none focus:border-slate-400">
                      <option value="week">Bu Hafta</option>
                      <option value="month">Bu Ay</option>
                      <option value="year">Bu Yıl</option>
                    </select>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="flex h-full flex-col items-center justify-center bg-transparent px-4 py-12">
      <p className="text-[13px] text-slate-400">Bu alanı görüntüleme yetkiniz bulunmuyor.</p>
    </div>
  );

  const tabs: TabItemProps[] = [
    { type: "custom", title: "Odak", path: "focus", tabId: "focus", customComponent: focusPanel },
    { type: "custom", title: "Aktivite", path: "activity", tabId: "activity", customComponent: activityPanel },
    { type: "custom", title: "Liderlik", path: "leaderboard", tabId: "leaderboard", customComponent: leaderboardPanel },
  ];

  if (canAdmin) tabs.push({ type: "custom", title: "Raporlar", path: "admin", tabId: "admin", customComponent: adminPanel });

  return (
    <TabPage
      title="Zaman Merkezi"
      tabs={tabs}
      rootClassName="bg-slate-50"
      navClassName="border-b border-slate-200 bg-white"
      titleClassName="text-slate-900 tracking-tight"
      tabButtonClassName="pb-3 text-sm transition border-b-2 -mb-px"
      tabActiveClassName="border-indigo-600 text-slate-900 font-semibold"
      tabInactiveClassName="border-transparent text-slate-700 font-medium hover:text-slate-900 hover:border-slate-300"
    />
  );
}
